import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/** @jsxImportSource @emotion/react */
import React from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTheme } from '@mui/material/styles';
import { colors, Portal } from '@mui/material';
import shadows from '@mui/material/styles/shadows';
import { MaterialDesignContent, SnackbarProvider as NotistackProvider, } from 'notistack';
export const DEFAULT_SNACKBAR_DURATION = 3000;
export const SnackbarConfigContext = React.createContext({ defaultErrorMsg: '' });
export const useSnackbarConfig = () => React.useContext(SnackbarConfigContext);
function SnackbarConfigProvider(props) {
    const { children, defaultErrorMsg } = props;
    return (_jsx(SnackbarConfigContext.Provider, { value: { defaultErrorMsg }, children: children }));
}
/**
 * This provider collects snackbars that are enqueued from anywhere in your app,
 * displays them, and provided default setting for them.
 * @param props - The props for the SnackbarProvider, along with SnackbarConfig for
 * customizing useSnackbar defaults.
 */
export function SnackbarProvider(props) {
    const { children, iconVariant, Components, classes, defaultErrorMsg, ...rest } = props;
    const containerElRef = React.useRef(null);
    return (_jsxs(_Fragment, { children: [_jsx(Portal, { children: _jsx("div", { id: "notistack-container", ref: containerElRef, css: { position: 'relative' } }) }), _jsx(SnackbarConfigProvider, { defaultErrorMsg: defaultErrorMsg, children: _jsx(NotistackProvider, { domRoot: containerElRef.current ?? undefined, autoHideDuration: DEFAULT_SNACKBAR_DURATION, anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    }, ...rest, iconVariant: {
                        success: _jsx(SnackCheckIcon, {}),
                        info: _jsx(SnackCheckIcon, { htmlColor: colors.blue[800] }),
                        ...iconVariant,
                    }, Components: {
                        default: DefaultSnackbar,
                        success: SuccessSnackbar,
                        info: InfoSnackbar,
                        ...Components,
                    }, classes: {
                        containerAnchorOriginTopRight: 'right-0',
                        containerRoot: 'font-bold',
                        ...classes,
                    }, children: children }) })] }));
}
function SnackCheckIcon(props) {
    const { sx = {}, ...rest } = props;
    const theme = useTheme();
    return (_jsx(CheckCircleIcon, { sx: {
            marginRight: theme.spacing(1),
            height: '18px',
            width: '18px',
            alignSelf: 'baseline',
            ...sx,
        }, ...rest }));
}
const DefaultSnackbar = React.forwardRef(function DefaultSnackbar(props, ref) {
    return (_jsx(MaterialDesignContent, { ...props, ref: ref, css: {
            backgroundColor: colors.grey[900],
            fontFamily: 'proxima-nova',
            fontWeight: 700,
            fontSize: 16,
            lineHeight: 1.5,
            borderRadius: 10,
        } }));
});
const SuccessSnackbar = React.forwardRef(function SuccessSnackbar(props, ref) {
    return (_jsx(MaterialDesignContent, { ...props, ref: ref, css: {
            backgroundColor: colors.green[500],
            borderRadius: 0,
            right: 0,
        } }));
});
const InfoSnackbar = React.forwardRef(function InfoSnackbar(props, ref) {
    return (_jsx(MaterialDesignContent, { ...props, ref: ref, css: {
            backgroundColor: '#fff !important',
            color: `${colors.grey[800]} !important`,
            borderRadius: `0px !important`,
            right: 0,
            maxWidth: 400,
            boxShadow: `${shadows[9]} !important`,
        } }));
});
